// A visual the server built from a card's or insight's own figures (report_narrative's
// validated `visual`): bars, a line over time, or a timeline of dated events. The server
// keeps one only when every figure is stated in its source; this re-checks the shape.

const KINDS = new Set(["bars", "line", "timeline"]);
const MIN_POINTS = 3;
const MAX_POINTS = 12;
const DATE = /^\d{4}(?:-\d{2}(?:-\d{2})?)?$/;

/** A YYYY, YYYY-MM or YYYY-MM-DD date that exists on the calendar ("2026-02-30" does not). */
function validDate(d) {
  if (!DATE.test(d)) return false;
  const [y, m, day] = d.split("-").map(Number);
  if (m === undefined) return true;
  if (m < 1 || m > 12) return false;
  if (day === undefined) return true;
  const t = new Date(Date.UTC(y, m - 1, day));
  return t.getUTCMonth() === m - 1 && t.getUTCDate() === day;
}

/**
 * The visual, normalized; `null` when it is missing or malformed.
 * @param {any} raw `{kind, title, unit, points: [{label, value} | {label, date}]}`
 * @returns {{kind: string, title: string, unit: string, points: Array<{label: string, value?: number, date?: string}>}|null}
 */
export function visualOf(raw) {
  if (!raw || typeof raw !== "object" || !KINDS.has(raw.kind) || !Array.isArray(raw.points))
    return null;
  const points = [];
  for (const pt of raw.points) {
    const label = typeof pt?.label === "string" ? pt.label.trim() : "";
    if (!label) return null;
    if (raw.kind === "timeline") {
      if (typeof pt.date !== "string" || !validDate(pt.date)) return null;
      points.push({ label, date: pt.date });
    } else {
      if (typeof pt.value !== "number" || !Number.isFinite(pt.value)) return null;
      points.push({ label, value: pt.value });
    }
  }
  if (points.length < MIN_POINTS || points.length > MAX_POINTS) return null;
  const unit = typeof raw.unit === "string" ? raw.unit.trim() : "";
  // Bars or a line whose every value is a bare year are dates, not amounts: draw a timeline.
  if (raw.kind !== "timeline" && yearsOnly(points, unit))
    return visualOf({
      ...raw,
      kind: "timeline",
      unit: "",
      points: points.map((p) => ({ label: p.label, date: String(p.value) })),
    });
  if (raw.kind === "timeline") points.sort((a, b) => (a.date < b.date ? -1 : 1));
  return {
    kind: raw.kind,
    title: typeof raw.title === "string" ? raw.title.trim() : "",
    unit: typeof raw.unit === "string" ? raw.unit.trim() : "",
    points,
  };
}

/** Every value a whole year 1900–2100, in no unit but years. */
function yearsOnly(points, unit) {
  return (
    (!unit || /^years?$/i.test(unit)) &&
    points.every((p) => Number.isInteger(p.value) && p.value >= 1900 && p.value <= 2100)
  );
}

/** A figure in the visual's unit: 29 + "%" → "+29%" (signed when the set has negatives). */
export function formatVisualValue(value, unit, signed = false) {
  // From 10,000 up a figure is abbreviated ("2.85M") so it fits a compact chart's value column.
  const n =
    Math.abs(value) >= 10000
      ? value.toLocaleString("en-US", { notation: "compact", maximumFractionDigits: 2 })
      : Math.abs(value) >= 1000
        ? value.toLocaleString("en-US", { maximumFractionDigits: 1 })
        : String(+value.toFixed(2));
  const sign = signed && value > 0 ? "+" : "";
  if (unit === "$") return `${value < 0 ? "-" : sign}$${n.replace("-", "")}`;
  if (unit === "%") return `${sign}${n}%`;
  return `${sign}${n}${unit && unit !== "units" ? ` ${unit}` : ""}`;
}
